// src/context/DialogContext.jsx
import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useRef,
  useEffect,
  useMemo,
} from "react";
import DialogModal from "components/DialogModal";

const DialogContext = createContext(null);

export function DialogProvider({ children }) {
  const [dialog, setDialog] = useState(null);

  // Keep the latest resolver without tying closeDialog to dialog in deps
  const resolveRef = useRef(null);

  // If dialog gets cleared for any reason, drop the resolver
  useEffect(() => {
    if (!dialog) resolveRef.current = null;
  }, [dialog]);

  const showDialog = useCallback((options) => {
    return new Promise((resolve) => {
      resolveRef.current = resolve;
      setDialog({
        ...options,
      });
    });
  }, []);

  const closeDialog = useCallback((value) => {
    const resolve = resolveRef.current;
    resolveRef.current = null;

    if (typeof resolve === "function") resolve(value);
    setDialog(null);
  }, []);

  // Native-like helpers for fast migration
  const api = useMemo(() => {
    const alert = async (message, opts = {}) => {
      await showDialog({
        title: opts.title || "Notice",
        message,
        buttons: [
          { value: "ok", label: opts.okLabel || "OK", variant: "primary" },
        ],
        defaultAction: "ok",
        ...opts,
      });
    };

    const confirm = async (message, opts = {}) => {
      const res = await showDialog({
        title: opts.title || "Confirm",
        message,
        buttons: [
          {
            value: "cancel",
            label: opts.cancelLabel || "Cancel",
            variant: "ghost",
          },
          { value: "yes", label: opts.okLabel || "OK", variant: "primary" },
        ],
        defaultAction: "yes",
        ...opts,
      });
      return res === "yes" || res === true;
    };

    const prompt = async (message, opts = {}) => {
      const res = await showDialog({
        title: opts.title || "Input",
        message,
        input: {
          label: opts.label,
          placeholder: opts.placeholder,
          defaultValue: opts.defaultValue ?? "",
          helpText: opts.helpText,
          ariaLabel: opts.ariaLabel,
          autoFocus: opts.autoFocus,
        },
        buttons: [
          {
            value: "cancel",
            label: opts.cancelLabel || "Cancel",
            variant: "ghost",
          },
          { value: "ok", label: opts.okLabel || "OK", variant: "primary" },
        ],
        defaultAction: "ok",
        ...opts,
      });

      // DialogModal returns:
      // - a string value (e.g. "cancel", "ok", "yes") when no input
      // - { value, inputValue } when input is present
      if (!res) return null;

      if (typeof res === "string") {
        if (res === "ok") return "";
        return null;
      }

      if (res?.value === "ok") {
        return typeof res.inputValue === "string" ? res.inputValue : "";
      }

      return null;
    };

    return {
      showDialog,
      closeDialog,
      dialog,
      alert,
      confirm,
      prompt,
    };
  }, [showDialog, closeDialog, dialog]);

  return (
    <DialogContext.Provider value={api}>
      {children}
      <DialogModal dialog={dialog} closeDialog={closeDialog} />
    </DialogContext.Provider>
  );
}

export function useDialog() {
  const ctx = useContext(DialogContext);
  if (!ctx) throw new Error("useDialog must be used within DialogProvider");
  return ctx;
}